import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecentLicences } from '../../services/dashboard';

const RequestedLicencesTable = () => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('organization');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('kanban');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentLicences();
  }, []);

  const loadRecentLicences = async () => {
    try {
      setLoading(true);
      const response = await getRecentLicences(10);
      if (response && response.success !== false && response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Failed to load recent licences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const normalizeStatus = (status) => {
    if (!status) return 'Other';
    if (status === 'Approved' || status === 'Granted') return 'Approved';
    if (status === 'Pending' || status === 'Awaiting Review') return 'Pending';
    if (status === 'Rejected') return 'Rejected';
    if (status === 'Under Review' || status === 'Self Reviewed') return 'Under Review';
    return status;
  };

  const kanbanColumns = [
    { key: 'Pending', title: 'Pending', accent: 'bg-amber-500' },
    { key: 'Under Review', title: 'Under Review', accent: 'bg-blue-500' },
    { key: 'Approved', title: 'Approved', accent: 'bg-emerald-500' },
    { key: 'Rejected', title: 'Rejected', accent: 'bg-red-500' },
    { key: 'Other', title: 'Other', accent: 'bg-gray-500' },
  ];

  const groupedByStatus = data.reduce((acc, item) => {
    const statusKey = normalizeStatus(item.status);
    if (!acc[statusKey]) acc[statusKey] = [];
    acc[statusKey].push(item);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-[12px] border border-[#f2f2f2] overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Requested Licences</h3>
          <div className="inline-flex items-center rounded-lg border border-gray-200 p-1 bg-gray-50">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Kanban
            </button>
          </div>
        </div>

        {viewMode === 'table' ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th 
                    className="text-left py-3 px-4 font-medium text-gray-900 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('organization')}
                  >
                    Organisation
                    {sortField === 'organization' && (
                      <span className="ml-2">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-left py-3 px-4 font-medium text-gray-900 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('licenceName')}
                  >
                    Licence Name
                    {sortField === 'licenceName' && (
                      <span className="ml-2">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-left py-3 px-4 font-medium text-gray-900 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('product')}
                  >
                    Product
                    {sortField === 'product' && (
                      <span className="ml-2">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-left py-3 px-4 font-medium text-gray-900 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('amount')}
                  >
                    Amount
                    {sortField === 'amount' && (
                      <span className="ml-2">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-left py-3 px-4 font-medium text-gray-900 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('status')}
                  >
                    Status
                    {sortField === 'status' && (
                      <span className="ml-2">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : sortedData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-gray-500">
                      No recent licence requests
                    </td>
                  </tr>
                ) : (
                  sortedData.map((row) => (
                    <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/licences/${row.id}`)}>
                      <td className="py-3 px-4 text-gray-900">{row.organization}</td>
                      <td className="py-3 px-4 text-gray-700">{row.licenceName}</td>
                      <td className="py-3 px-4 text-gray-700">{row.product}</td>
                      <td className="py-3 px-4 text-gray-900 font-medium">{row.amount}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(row.status)}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/licences/${row.id}`);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : loading ? (
          <div className="py-8 text-center text-gray-500">Loading...</div>
        ) : data.length === 0 ? (
          <div className="py-8 text-center text-gray-500">No recent licence requests</div>
        ) : (
          <div className="overflow-x-auto pb-2">
            <div className="grid grid-cols-5 gap-4 min-w-[980px]">
              {kanbanColumns.map((column) => {
                const rows = groupedByStatus[column.key] || [];
                return (
                  <div key={column.key} className="rounded-xl border border-gray-200 bg-gray-50/60">
                    <div className="px-3 py-2 border-b border-gray-200 bg-white rounded-t-xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${column.accent}`} />
                          <h4 className="text-sm font-semibold text-gray-800">{column.title}</h4>
                        </div>
                        <span className="text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">
                          {rows.length}
                        </span>
                      </div>
                    </div>

                    <div className="p-3 space-y-3 min-h-[220px] max-h-[430px] overflow-y-auto">
                      {rows.length === 0 ? (
                        <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg py-6 text-center bg-white">
                          No items
                        </div>
                      ) : (
                        rows.map((row) => (
                          <button
                            type="button"
                            key={row.id}
                            onClick={() => navigate(`/licences/${row.id}`)}
                            className="w-full text-left p-3 rounded-lg border border-gray-200 bg-white hover:shadow-sm hover:border-emerald-200 transition"
                          >
                            <p className="text-xs text-gray-500 mb-1 truncate">{row.organization}</p>
                            <p className="text-sm font-medium text-gray-900 mb-2 break-words">
                              {row.licenceName}
                            </p>
                            <div className="flex items-center justify-between gap-2">
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusColor(row.status)}`}>
                                {row.status}
                              </span>
                              <span className="text-xs font-semibold text-gray-700">{row.amount}</span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestedLicencesTable;